import { Box, Text } from "@mantine/core"
import { ReactNode } from "react"
import {
  DeliveryProgressBadge, InvoiceStatusBadge, PurchaseTypeBadge,
} from "../../../components/order/status-badges"
import RenderText from "../../../components/ui/render-text"
import { IOrderPlus } from "../../../models/Order"
import { currencyFormat } from "../../../utils/currency"
import { dateTimeSecFormat } from "../../../utils/date"
import { renderBusinessName } from "../../../utils/render-business-name"
import { renderName } from "../../../utils/render-name"
import { orderStatusPill, paymentStatusPill } from "./StatusPill"

export type CellCtx = { typeName: (code?: string | null) => string | undefined }

export type ColumnDef = {
  id: string
  /** i18n key in the supplierSales namespace */
  label: string
  /** Backend sort key; columns without one are not sortable. */
  sort?: string
  align?: "right"
  /** Hidden by default, switched on from the «Багана» menu. */
  optional?: boolean
  /** Only on the «Хэсэгчлэн хүргэх» tab (SPLIT_COLUMN_IDS); never a default or optional main-tab column. */
  splitOnly?: boolean
  render: (r: IOrderPlus, ctx: CellCtx) => ReactNode
}

const dash = (v: ReactNode) => <RenderText text={v as string} />

/**
 * Default columns follow Ord_SalesList (order, type, status, customer, delivery date, amount, PO); the former
 * 39-column table survives as optional columns. Payment is optional since 2026-11-04: no Борлуулалт action depends on
 * it, a prepayment wait already shows as «Баталсан», and payment follow-up lives in the invoice module.
 */
export const SALES_COLUMNS: ColumnDef[] = [
  {
    id: "order", label: "sales.columns.order", sort: "createdAt",
    render: (r) => (
      <Box>
        <Text ff="monospace" fw={700} fz={14}>{r.salesCode ?? "-"}</Text>
        <Text fz={14} c="gray.8">{dateTimeSecFormat(r.createdAt)}</Text>
      </Box>
    ),
  },
  { id: "type", label: "sales.type", render: (r, c) => dash(c.typeName(r.type)) },
  // D16: Борлуулалт is about delivery — the main «Төлөв» column shows the delivery progress, the order status itself sits
  // in the «Захиалгын төлөв» column on the right (same value Худалдан авалт shows). The split tab keeps the order status.
  { id: "delivery", label: "sales.deliveryStatus", sort: "deliveryNoteStatus", render: (r) => <DeliveryProgressBadge order={r} /> },
  { id: "status", label: "sales.columns.status", sort: "orderStatus", splitOnly: true, render: (r) => orderStatusPill(r.orderStatus) },
  {
    id: "customer", label: "sales.columns.customer", sort: "receiverBusinessRefCodeProfileName",
    render: (r) => (
      <Box>
        <Text fz={14} fw={600}>{renderBusinessName(r.receiverBusiness, "") || "-"}</Text>
        <Text fz={14} c="gray.8" ff="monospace">#{r.receiverBusiness?.partner?.refCode || r.receiverBusiness?.refCode || "-"}</Text>
      </Box>
    ),
  },
  { id: "deliveryDate", label: "sales.columns.deliveryDate", sort: "deliveryDate", render: (r) => dash(r.deliveryDate ? dateTimeSecFormat(r.deliveryDate) : null) },
  { id: "amount", label: "sales.columns.amount", sort: "totalAmount", align: "right", render: (r) => <Text fz={14} fw={700} ff="monospace" ta="right" miw="max-content">{currencyFormat(r.totalAmount || 0)}</Text> },
  {
    // Shows the invoice's payment status. No `sort`: the backend would sort by the order row's paymentStatus, which
    // is only written on full payment and can disagree with what this cell shows.
    id: "payment", label: "sales.columns.payment", optional: true,
    render: (r) => paymentStatusPill(r.invoice?.paymentStatus),
  },
  {
    // Order status for every row (an own «Шинэ захиалга» order has no PO but still has a status); the PO number on top
    // when the order came from Худалдан авалт.
    id: "po", label: "sales.columns.orderStatus", sort: "orderStatus",
    render: (r) => (
      <Box>
        {r.purchaseCode && <Text ff="monospace" fz={14} fw={600}>{r.purchaseCode}</Text>}
        {orderStatusPill(r.orderStatus)}
      </Box>
    ),
  },
  { id: "regNumber", label: "sales.regNumber", optional: true, sort: "receiverBusinessRegNumber", render: (r) => dash(r.receiverBusiness?.regNumber) },
  { id: "branch", label: "sales.branch", optional: true, sort: "receiverBranchName", render: (r) => dash(r.receiverBranch?.name) },
  {
    id: "receiverStaff", label: "sales.staff", optional: true, sort: "receiverStaffFirstNamePhone",
    render: (r) => (
      <Box>
        <RenderText text={renderName(r.receiverStaff)} />
        <RenderText text={r.receiverStaff?.phone} />
      </Box>
    ),
  },
  { id: "region", label: "sales.region", optional: true, render: (r) => dash(r.region?.name) },
  { id: "direction", label: "sales.direction", optional: true, render: (r) => dash(r.direction?.name) },
  {
    id: "courier", label: "sales.deliveryStaff", optional: true, sort: "deliveryStaffFirstNameEmail",
    render: (r) => (
      <Box>
        <RenderText text={renderName(r.deliveryNote?.staff)} />
        <RenderText text={r.deliveryNote?.staff?.email} />
      </Box>
    ),
  },
  { id: "deliveredDate", label: "sales.deliveredDate", optional: true, sort: "deliveryNoteDeliveryDate", render: (r) => dash(r.deliveryNote?.deliveryDate ? dateTimeSecFormat(r.deliveryNote.deliveryDate) : null) },
  { id: "paidAmount", label: "sales.paidAmount", optional: true, sort: "paidAmount", align: "right", render: (r) => <RenderText text={r.paidAmount || 0} isAmount fontWeight={600} /> },
  { id: "amountToPay", label: "sales.amountToPay", optional: true, sort: "amountToPay", align: "right", render: (r) => <RenderText text={r.amountToPay || 0} isAmount fontWeight={600} /> },
  { id: "paymentTerm", label: "sales.paymentTerm", optional: true, sort: "paymentTermDescription", render: (r) => dash(r.paymentTerm?.description) },
  { id: "invoiceNo", label: "sales.invoiceNo", optional: true, render: (r) => dash(r.invoice?.refCode) },
  { id: "invoiceStatus", label: "sales.invoiceStatus", optional: true, render: (r) => <InvoiceStatusBadge code={r.invoice?.invoiceStatus} /> },
  { id: "poType", label: "sales.poType", optional: true, sort: "purchaseType", render: (r) => <PurchaseTypeBadge code={r.purchaseType} /> },
  { id: "respondedDate", label: "sales.poDate", optional: true, sort: "respondedDate", render: (r) => dash(r.respondedDate ? dateTimeSecFormat(r.respondedDate) : null) },
  { id: "orderedUser", label: "sales.ordered", optional: true, sort: "orderedUserFirstName", render: (r) => dash(renderName(r.orderedUser)) },
  { id: "reviewedUser", label: "sales.reviewed", optional: true, render: (r) => dash(renderName(r.reviewedUser)) },
]

export const DEFAULT_COLUMN_IDS = SALES_COLUMNS.filter((c) => !c.optional && !c.splitOnly).map((c) => c.id)

/** Хэсэгчлэн хүргэх tab: the same table, without the payment and PO columns (the split API does not carry them). */
export const SPLIT_COLUMN_IDS = ["order", "type", "status", "customer", "deliveryDate"]
